// Pass 2.12 — hand-rolled resize handle.
// Pass 2.12.1 — growsTo prop fixes inverted thread-drawer drag.
//
// Pixel-native, no external library. Pre-2.11 we tried
// react-resizable-panels v2 and shipped broken twice; the lib is
// percentage-native and that mismatch caused the persistence
// drift we never fully closed. This component does one thing:
// translate horizontal pointer drag into pixel-width updates,
// clamped to [minWidth, maxWidth]. The caller owns the actual
// width state (BrowseView via useLayoutState) and is the only
// place that knows about persistence.
//
// growsTo: which side the resized pane is on relative to the
//   handle. 'right' (default) — pane is to the LEFT of the handle
//   so dragging the handle right grows the pane (sidebar case).
//   'left' — pane is to the RIGHT of the handle so dragging right
//   SHRINKS it (thread-drawer case). Only the mouse delta sign
//   flips; keyboard semantics are unchanged ("ArrowLeft shrinks,
//   ArrowRight grows" from the pane's perspective regardless).
//
// Interaction:
//   pointerdown   → e.preventDefault() (suppress text select);
//                   setPointerCapture; record startX + startWidth.
//   pointermove   → delta = clientX - startX; if growsTo='left'
//                   negate delta; newWidth = clamp(startWidth +
//                   delta, min, max); call onResize(newWidth).
//   pointerup /
//   pointercancel → releasePointerCapture; clear local state.
//   ArrowLeft     → onResize(currentWidth - 8)  // also clamped
//   ArrowRight    → onResize(currentWidth + 8)
//   PageUp        → onResize(currentWidth - 32)
//   PageDown      → onResize(currentWidth + 32)
//   Home          → onResize(minWidth)
//   End           → onResize(maxWidth)
//
// Accessibility:
//   role="separator" + aria-orientation="vertical".
//   aria-valuemin/max/now on the handle so screen readers
//   announce the resize range and current value.
//   tabIndex=0 so the handle is keyboard-focusable.
//
// Why pointerdown's preventDefault matters: without it, a drag
// across text content selects highlighted text alongside the
// resize, which (a) looks broken and (b) on mouseup the browser
// might consume the click and prevent our handlers from clearing.

import {
  useCallback,
  useRef,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

const KEYBOARD_STEP_PX = 8;
const KEYBOARD_PAGE_PX = 32;

interface ResizeHandleProps {
  orientation: "horizontal";
  /** Called whenever the user requests a new width (drag tick or
   *  keyboard step). The caller is responsible for clamping; this
   *  component also clamps before calling so a misbehaving caller
   *  can't accidentally store an out-of-range value. */
  onResize: (newWidth: number) => void;
  /** Called once at pointerdown / keystroke start. The component
   *  doesn't track the live width itself — the caller's state is
   *  the source of truth — so the start anchor is always read
   *  fresh on the next interaction. */
  getCurrentWidth: () => number;
  minWidth: number;
  maxWidth: number;
  /** Pass 2.12.1: which side the resized pane is on relative to
   *  the handle. 'right' (default) for sidebar (pane on the LEFT
   *  of the handle); 'left' for the thread drawer (pane on the
   *  RIGHT of the handle). Only flips mouse delta sign — keyboard
   *  semantics stay "ArrowLeft shrinks, ArrowRight grows" from
   *  the pane's perspective. */
  growsTo?: "left" | "right";
  /** Required — every focusable interactive element needs an
   *  accessible name. */
  "aria-label": string;
  /** Optional — passed through to the rendered div for testing. */
  "data-testid"?: string;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function ResizeHandle({
  orientation,
  onResize,
  getCurrentWidth,
  minWidth,
  maxWidth,
  growsTo = "right",
  "aria-label": ariaLabel,
  "data-testid": testid,
}: ResizeHandleProps) {
  // Drag anchor — the (clientX, width) pair captured at pointerdown.
  // null when not dragging. Held in a ref so re-renders during the
  // drag don't reset the anchor.
  const anchorRef = useRef<{ x: number; width: number } | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // Suppress text selection that would otherwise trigger as
      // the pointer drags across surrounding text content. Also
      // cancels the browser's default focus-shift, which we'd
      // immediately undo with our own focus management anyway.
      e.preventDefault();
      const target = e.currentTarget;
      try {
        target.setPointerCapture(e.pointerId);
      } catch {
        // Some headless test environments don't implement
        // setPointerCapture cleanly; the fallback (document-level
        // listeners) would take care of it. Swallow.
      }
      anchorRef.current = { x: e.clientX, width: getCurrentWidth() };
      target.dataset.dragging = "true";
    },
    [getCurrentWidth],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const anchor = anchorRef.current;
      if (anchor === null) return;
      const rawDelta = e.clientX - anchor.x;
      // For panes that grow leftward (the resized pane sits to
      // the RIGHT of the handle, e.g. thread drawer), the handle
      // moving right should SHRINK the pane — flip the sign.
      const delta = growsTo === "right" ? rawDelta : -rawDelta;
      const newWidth = clamp(anchor.width + delta, minWidth, maxWidth);
      onResize(newWidth);
    },
    [minWidth, maxWidth, onResize, growsTo],
  );

  const endDrag = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (anchorRef.current !== null) {
        try {
          target.releasePointerCapture(e.pointerId);
        } catch {
          // Same fallback as setPointerCapture.
        }
        anchorRef.current = null;
        delete target.dataset.dragging;
      }
    },
    [],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const cur = getCurrentWidth();
      let next: number | null = null;
      switch (e.key) {
        case "ArrowLeft":
          next = cur - KEYBOARD_STEP_PX;
          break;
        case "ArrowRight":
          next = cur + KEYBOARD_STEP_PX;
          break;
        case "PageUp":
          next = cur - KEYBOARD_PAGE_PX;
          break;
        case "PageDown":
          next = cur + KEYBOARD_PAGE_PX;
          break;
        case "Home":
          next = minWidth;
          break;
        case "End":
          next = maxWidth;
          break;
        default:
          return;
      }
      e.preventDefault();
      onResize(clamp(next, minWidth, maxWidth));
    },
    [getCurrentWidth, minWidth, maxWidth, onResize],
  );

  const currentWidth = getCurrentWidth();

  return (
    <div
      ref={handleRef}
      role="separator"
      aria-orientation={orientation === "horizontal" ? "vertical" : "horizontal"}
      aria-label={ariaLabel}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-valuenow={currentWidth}
      tabIndex={0}
      data-testid={testid}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      // Visual: 4px wide vertical line. Default uses border-default
      // (visible at rest, unlike Pass 2.11's bg-transparent) and
      // brightens on hover/drag. cursor-col-resize is what users
      // see when they hover; tab-focused users get the role and
      // ARIA attributes above.
      className="w-1 cursor-col-resize bg-theme-border-default hover:bg-theme-border-active data-[dragging=true]:bg-theme-border-active flex-shrink-0 transition-colors duration-150"
    />
  );
}
